export type TemplateDescriptionFormatAction =
  | "bold"
  | "italic"
  | "bullet"
  | "number"
  | "quote"
  | "code"
  | "link";

type ApplyFormatResult = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
};

const wrapSelection = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  prefix: string,
  suffix: string,
  placeholder: string,
): ApplyFormatResult => {
  const before = value.slice(0, selectionStart);
  const selected = value.slice(selectionStart, selectionEnd);
  const after = value.slice(selectionEnd);
  const content = selected || placeholder;
  const nextValue = `${before}${prefix}${content}${suffix}${after}`;
  const nextSelectionStart = selectionStart + prefix.length;
  const nextSelectionEnd = nextSelectionStart + content.length;

  return {
    value: nextValue,
    selectionStart: nextSelectionStart,
    selectionEnd: nextSelectionEnd,
  };
};

const prefixLines = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  prefixFactory: (index: number) => string,
  placeholder: string,
): ApplyFormatResult => {
  const before = value.slice(0, selectionStart);
  const selected = value.slice(selectionStart, selectionEnd);
  const after = value.slice(selectionEnd);
  const source = selected || placeholder;
  const lines = source.split("\n");
  const prefixed = lines.map((line, index) => `${prefixFactory(index)}${line}`);
  const content = prefixed.join("\n");
  const nextValue = `${before}${content}${after}`;

  return {
    value: nextValue,
    selectionStart,
    selectionEnd: selectionStart + content.length,
  };
};

export const applyTemplateDescriptionFormat = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  action: TemplateDescriptionFormatAction,
): ApplyFormatResult => {
  switch (action) {
    case "bold":
      return wrapSelection(
        value,
        selectionStart,
        selectionEnd,
        "**",
        "**",
        "bold text",
      );
    case "italic":
      return wrapSelection(
        value,
        selectionStart,
        selectionEnd,
        "_",
        "_",
        "italic text",
      );
    case "code": {
      const selected = value.slice(selectionStart, selectionEnd);
      if (selected.includes("\n")) {
        return wrapSelection(
          value,
          selectionStart,
          selectionEnd,
          "```\n",
          "\n```",
          "code block",
        );
      }

      return wrapSelection(
        value,
        selectionStart,
        selectionEnd,
        "`",
        "`",
        "inline code",
      );
    }
    case "bullet":
      return prefixLines(
        value,
        selectionStart,
        selectionEnd,
        () => "- ",
        "List item",
      );
    case "number":
      return prefixLines(
        value,
        selectionStart,
        selectionEnd,
        (index) => `${index + 1}. `,
        "List item",
      );
    case "quote":
      return prefixLines(
        value,
        selectionStart,
        selectionEnd,
        () => "> ",
        "Quoted text",
      );
    case "link":
      return wrapSelection(
        value,
        selectionStart,
        selectionEnd,
        "[",
        "](https://example.com)",
        "Link text",
      );
    default:
      return {
        value,
        selectionStart,
        selectionEnd,
      };
  }
};

export const stripTemplateDescriptionMarkdown = (
  value?: string | null,
): string => {
  if (!value?.trim()) {
    return "";
  }

  return value
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/_(.*?)_/g, "$1")
    .replace(/```[\s\S]*?```/g, "[code]")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/^>\s+/gm, "")
    .replace(/^[\s]*[-*+]\s+/gm, "• ")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    .replace(/\n\s*\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
